import { Navigate, Outlet } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useInjection } from 'inversify-react';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import LoadingScreen from '@shared/components/LoadingScreen';
import { PATH_CABINET } from '../paths';

/**
 * Гвард раздела /osi: «дом выбран?» (OSI-806).
 *
 * ПРОБЛЕМА:
 * Пользователь авторизован (токен есть — AuthGuard его пропустил), но дом НЕ выбран:
 * в localStorage `OsiService = {"osiId":null}`. `OsiServiceImpl.hydrateOsi()` при пустом
 * osiId делает ранний `return`, поэтому `osiInfo` не загружается НИКОГДА. А все страницы
 * раздела (root, info, abonents, accruals, osv, payments, reports, invoices, debts, acts)
 * начинаются с раннего `return <LoadingScreen/>` по отсутствию `osiInfo` — в результате
 * весь раздел /osi висел вечным спиннером (а до этого — пустым каркасом).
 * Правильное поведение: увести в /cabinet, где дом и выбирается (OsiList → selectOsi).
 *
 * ТРИ СОСТОЯНИЯ (порядок проверок важен):
 * 1. Persist-стор ЕЩЁ НЕ ГИДРАТИРОВАН — `osiId` восстанавливается из localStorage асинхронно,
 *    на первом рендере он пуст даже у пользователя с выбранным домом. Здесь показываем лоадер;
 *    решение «дома нет» принимать нельзя, иначе выкинем в /cabinet того, у кого дом есть.
 * 2. ГИДРАТИРОВАН, `osiId` ПУСТ — дом действительно не выбран → редирект на /cabinet.
 *    `replace` обязателен: иначе «Назад» вернёт на /osi и всё повторится (цикл в истории).
 * 3. ГИДРАТИРОВАН, `osiId` ЕСТЬ — пропускаем раздел, дальше всё работает как раньше:
 *    `hydrateOsi()` догружает `osiInfo`, страницы до этого момента показывают свой лоадер.
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ ГВАРД, А НЕ ПРОВЕРКА ВНУТРИ OsiLayout:
 * решения «кого куда пускаем» собраны в одном месте (routes/guards) рядом с AuthGuard,
 * layout остаётся презентационным, и при заведомом редиректе не монтируются Navbar/Sidebar
 * раздела ОСИ, которые всё равно рисуют `null` без `osiInfo`.
 *
 * ЦИКЛА /osi → /cabinet → /osi НЕТ: страница /cabinet (pages/cabinet/ui) никуда не редиректит,
 * переход в раздел ОСИ там происходит только по клику пользователя по карточке дома
 * (entities/cabinet/osiCard → `selectOsi()` → `goToOsiRoot()`), т.е. уже с непустым osiId.
 */
export const OsiSelectedGuard = observer(() => {
  const osiModule = useInjection<OsiService>(OsiServiceIdentifier);

  // (1) Гидратация persist-стора ещё не завершилась — ждём, ничего не решаем.
  if (!osiModule.isStoreHydrated) return <LoadingScreen />;

  // (2) Гидратация завершена, дома нет — уводим туда, где его выбирают.
  if (!osiModule.osiId) return <Navigate to={PATH_CABINET.root} replace />;

  // (3) Дом выбран — раздел работает как раньше.
  return <Outlet />;
});

export default OsiSelectedGuard;
